import pLimit from 'p-limit';
import { AxiosResponse } from 'axios';
import { UserType, UserRole } from '@upstart/harmony/lib/constants';

import UserService from '../../../../services/UserService';
import { RowWithErrors } from '../../../DataGrid';
import { Dealer } from '../../../../types/dealerTypes';
import { User } from '../../../../types/userTypes';

const MAX_CONCURRENT_REQUESTS = 5;

// launchpad uses a simplified role system, every user is either a sales person or a sales manager
// A sales person maps to UserRole.USER and a sales manager maps to UserRole.USER + UserRole.DESKING_MANAGER
export enum SimpleRole {
  SALESPERSON = 'Sales Person',
  SALESMANAGER = 'Sales Manager',
}

export const SimpleRoleMapping: { [index in SimpleRole]: UserRole[] } = {
  [SimpleRole.SALESPERSON]: [UserRole.USER],
  [SimpleRole.SALESMANAGER]: [UserRole.USER, UserRole.DESKING_MANAGER],
};

type NewUserProps = Pick<
  User,
  'firstName' | 'lastName' | 'email' | 'phone' | 'crmUserId' | 'roles'
>;

interface RowModelBase extends NewUserProps, RowWithErrors {
  id: string;
  userId?: number;
  isNew: boolean;
  isEdited: boolean;
  errors: Partial<Record<keyof NewUserProps, string>>;
}

interface NewUserRowModel extends RowModelBase {
  isNew: true;
  userId?: number;
}

interface ExistingUserRowModel extends RowModelBase {
  isNew: false;
  userId: number;
}

export type RowModel = NewUserRowModel | ExistingUserRowModel;

function makeEmptyNewUserObj(): NewUserProps {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    roles: [],
    crmUserId: '',
  };
}

let nextRowId = 0;
export function makeNewRow(
  userProps: NewUserProps = makeEmptyNewUserObj(),
): RowModel {
  nextRowId += 1;
  return {
    ...userProps,
    id: `row${nextRowId}`,
    isNew: true,
    isEdited: false,
    errors: {},
    blurredFields: new Set(),
  };
}

export function rowHasUserData(row: RowModel): boolean {
  type UserKey = keyof ReturnType<typeof makeEmptyNewUserObj>;
  return Object.keys(makeEmptyNewUserObj()).some((key) => {
    const rowVal = row[key as UserKey];
    return rowVal != null && rowVal.length > 0;
  });
}

function rowToUser(row: RowModel, dealerId: Dealer['id']): Partial<User> {
  const user: Partial<User> = {
    firstName: row.firstName,
    lastName: row.lastName,
    email: row.email,
    roles: row.roles,
    dealer_id: dealerId,
  };

  if (row.phone) {
    user.phone = row.phone;
  }
  if (row.crmUserId) {
    user.crmUserId = row.crmUserId;
  }
  if (row.userId !== undefined) {
    user.id = row.userId;
  }
  if (row.email) {
    user.username = row.email;
  }

  return user;
}

export function isNewUserRow(
  row: RowModel,
): row is NewUserRowModel & { email: string } {
  return !!row.email && row.isNew;
}

export function isExistingUserRow(
  row: RowModel,
): row is ExistingUserRowModel & { email: string } {
  // A row can be both new and edited, in that case the row is still considered new
  return !!row.email && !row.isNew;
}

async function settleIntoMap<T, R>(arr: T[], op: (arg: T) => Promise<R>) {
  // have: list of objs to make requests with
  // want: map from obj to settled result
  const map = new Map<T, PromiseSettledResult<R>>();
  const settled = await Promise.allSettled(arr.map(op));
  arr.forEach((item, index) => {
    map.set(item, settled[index]);
  });
  return map;
}

interface DoUserCrudReturn {
  rowToRes: Map<RowModel, PromiseSettledResult<AxiosResponse<User>>>;
  deletedRowToRes: Map<RowModel, PromiseSettledResult<AxiosResponse<unknown>>>;
}
export async function doUserCrud(
  rows: RowModel[],
  deletedRows: RowModel[],
  dealerId: Dealer['id'],
): Promise<DoUserCrudReturn> {
  const limit = pLimit(MAX_CONCURRENT_REQUESTS);

  const rowsToDelete = deletedRows
    .filter(rowHasUserData)
    .filter(isExistingUserRow);
  const deletedRowToRes = await settleIntoMap(rowsToDelete, (row) =>
    limit(() => UserService.delete(row.userId)),
  );

  const rowToRes = await settleIntoMap(
    rows.filter((row) => rowHasUserData(row) && (row.isNew || row.isEdited)),
    (row) => {
      if (isNewUserRow(row)) {
        return limit(() =>
          UserService.create({
            username: row.email,
            type: UserType.CLIENT,
            ...rowToUser(row, dealerId),
          }),
        );
      }
      // if this is not a new user row, it must be an existing user row as we filtered
      // out empty rows above
      return limit(() =>
        UserService.update(
          (row as ExistingUserRowModel).userId,
          rowToUser(row, dealerId),
        ),
      );
    },
  );

  await Promise.allSettled(rowToRes);

  return {
    rowToRes,
    deletedRowToRes,
  };
}

export function postProcessCrud(
  rows: RowModel[],
  deletedRows: RowModel[],
  crudResults: DoUserCrudReturn,
  errorCb: (row: RowModel, error: any) => void,
) {
  const updatedRows = rows
    .map((row) => {
      const res = crudResults.rowToRes.get(row);
      if (res === undefined) {
        return row;
      }

      if (res.status === 'fulfilled') {
        if (isNewUserRow(row)) {
          return {
            ...row,
            isEdited: false,
            isNew: false,
            userId: res.value.data.id,
          } as RowModel;
        }

        return { ...row, isEdited: false, isNew: false } as RowModel;
      }

      errorCb(row, res.reason);
      return row;
    })
    .filter((r): r is RowModel => r !== null);

  // if a response to delete a row was successful, remove it from the array
  const updatedDeletedRows: RowModel[] = [];
  deletedRows.forEach((row) => {
    const res = crudResults.deletedRowToRes.get(row);
    if (res === undefined) {
      return;
    }

    if (res.status === 'rejected') {
      errorCb(row, res.reason);
      updatedDeletedRows.push(row);
    }
  });

  return {
    updatedRows,
    updatedDeletedRows,
  };
}
